import { AUTH_STORAGE_KEY, supabaseClient } from './supabase';
import {
  IMPERSONATION_META_KEY,
  SAVED_SESSION_KEY,
  clearImpersonationRecoveryState,
  getImpersonationMeta,
  type ImpersonationMeta,
} from './impersonationStorage';

export { clearImpersonationRecoveryState, getImpersonationMeta };
export type { ImpersonationMeta };

/**
 * Super-admin impersonation. startImpersonation swaps the client session for a
 * REAL session belonging to the target user (via the admin-impersonate edge
 * function + magic-link OTP), so RLS/RBAC behave exactly as they would for
 * them. The admin's own session is stashed in localStorage and restored by
 * stopImpersonation.
 *
 * Both swaps are done by writing auth-js's storage key and hard-reloading rather
 * than by calling auth.setSession(). The app runs two GoTrueClient instances
 * (supabaseClient and supabaseCoreClient) over one storage key, and other tabs add
 * more. setSession only updates the instance it is called on, so every other one
 * keeps the old session in memory and writes it back to storage on its next auto
 * refresh — which is how an exit could land you back as the impersonated user. It
 * also takes the shared auth lock, so it can block on another instance. A reload
 * re-initialises every client from storage, the one piece of state they all agree
 * on, and needs neither the lock nor cooperation from the other instances.
 */

const SYNC_CHANNEL_NAME = 'sky-impersonation';

/** Set before we broadcast, so our own tab ignores the message and navigates instead. */
let isSwappingSession = false;

export async function signOutLocally() {
  try {
    return await supabaseClient.auth.signOut({ scope: 'local' });
  } finally {
    clearImpersonationRecoveryState();
  }
}

/**
 * Reloads this tab when another one starts or ends an impersonation. Without it a
 * background tab keeps rendering — and querying as — the session it loaded with,
 * and can overwrite the swapped-in session when its token comes up for refresh.
 * Returns an unsubscribe function.
 */
export function initImpersonationSync(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
  channel.onmessage = () => {
    if (isSwappingSession) return;
    window.location.reload();
  };
  return () => channel.close();
}

function broadcastSessionSwap(): void {
  isSwappingSession = true;
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
    channel.postMessage({ type: 'session-swapped' });
    channel.close();
  } catch {
    // Best effort — this tab still navigates below.
  }
}

export async function startImpersonation(targetAppUserId: number): Promise<void> {
  if (getImpersonationMeta()) throw new Error('Already impersonating — exit first.');

  // getSession() refreshes an expiring token and rewrites storage, so read the raw
  // blob afterwards: it is then current, and byte-for-byte what auth-js expects to
  // load back on restore.
  const { data: sessionData } = await supabaseClient.auth.getSession();
  if (!sessionData.session) throw new Error('No active session.');
  const savedSession = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!savedSession) throw new Error('Could not read the current session.');

  const { data, error } = await supabaseClient.functions.invoke('admin-impersonate', {
    body: { app_user_id: targetAppUserId },
  });
  if (error) {
    // supabase-js wraps non-2xx into a generic error; surface the body when possible.
    const context = (error as { context?: Response }).context;
    if (context) {
      const detail = await context.json().catch(() => null) as { error?: string } | null;
      throw new Error(detail?.error || error.message);
    }
    throw new Error(error.message);
  }
  const tokenHash = (data as { token_hash?: string })?.token_hash;
  const target = (data as { target?: { name: string | null; email: string } })?.target;
  if (!tokenHash || !target) throw new Error('Impersonation token missing.');

  // Save the admin session BEFORE verifying the OTP replaces it.
  localStorage.setItem(SAVED_SESSION_KEY, savedSession);

  try {
    localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify({
      targetName: target.name,
      targetEmail: target.email,
      startedAt: new Date().toISOString(),
    } satisfies ImpersonationMeta));
  } catch {
    localStorage.removeItem(SAVED_SESSION_KEY);
    throw new Error('Could not save impersonation recovery state.');
  }

  const { error: otpError } = await supabaseClient.auth.verifyOtp({
    type: 'magiclink',
    token_hash: tokenHash,
  });
  if (otpError) {
    localStorage.removeItem(SAVED_SESSION_KEY);
    localStorage.removeItem(IMPERSONATION_META_KEY);
    throw new Error(`Could not start impersonation: ${otpError.message}`);
  }

  broadcastSessionSwap();
  window.location.assign('/');
}

export async function stopImpersonation(): Promise<void> {
  const saved = localStorage.getItem(SAVED_SESSION_KEY);
  if (!saved) {
    // Nothing saved (cleared storage?) — sign out entirely so the admin can log back in.
    await signOutToLogin();
    return;
  }

  try {
    localStorage.setItem(AUTH_STORAGE_KEY, saved);
  } catch {
    // Could not hand the session back; leaving the admin as the target is the one
    // outcome to avoid, so drop them at the login screen instead.
    await signOutToLogin();
    return;
  }

  // Only now that the admin session is back in storage is it safe to clear the
  // banner. Clearing it first is what left an admin stranded as the target user
  // with no way out whenever the restore below failed.
  clearImpersonationRecoveryState();

  broadcastSessionSwap();
  window.location.replace('/admin/users');
}

async function signOutToLogin(): Promise<void> {
  try {
    // Local scope only: the impersonated user is a real person, and a global sign
    // out would revoke the sessions they are using on their own machine.
    await signOutLocally();
  } catch {
    // The session keys are cleared below regardless.
  }
  clearImpersonationRecoveryState();
  localStorage.removeItem(AUTH_STORAGE_KEY);
  broadcastSessionSwap();
  window.location.replace('/auth/login');
}
